import { Modal } from "antd";
import { Alert } from "components/elements";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import * as warningLetterServices from "../service/warningLetterRequestApi";
import * as commonServices from "features/requestApproval/service/pendingApprovalApi";
import { OTHER_REASON_ID } from "../schemas/WarningLetterRequestSchema";

function toDate(value) {
    return value ? dayjs(value) : null;
}

function useWarningLetterRequest() {

    const { t, i18n } = useTranslation();
    const currentEmployee = useSelector((state) => state.company.currentEmployee);
    const currentCompany = useSelector((state) => state.company.currentCompany);
    const [isLoading, setIsLoading] = useState(false);
    const [warningLetterRequestList, setWarningLetterRequestList] = useState([]);
    const [pagination, setPagination] = useState({ page: 0, size: 10 });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [employeeOptions, setEmployeeOptions] = useState([]);
    const [companyRegulationOptions, setCompanyRegulationOptions] = useState([]);
    const [modalMode, setModalMode] = useState(null);
    function toParams(params = {}) {
        const { pagination: p = {}, sortBy = [] } = params;
        return {
            pagination: {
                page: p.page,
                size: p.pageSize,
            },
            sortBy: (sortBy || [])
                .filter((s) => s.order !== undefined)
                .map((s) => ({
                    direction: s.order === "descend" ? "desc" : "asc",
                    property: s.field,
                })),
        };
    }

    const getWarningLetterRequestList = useCallback(async () => {
        try {
            setIsLoading(true);

            const size = 10;
            let page = 0;
            let totalPages = 1;
            let allData = [];

            do {
                const response = await warningLetterServices.getWarningLetterRequestPaging({
                    page,
                    size,
                    companyId: currentCompany,
                    employeeId: currentEmployee,
                });

                allData = [...allData, ...(response?.data?.content || [])];
                totalPages = response?.data?.totalPages || 1;
                page++;
            } while (page < totalPages);

            const approvedList = allData.filter(
                item => item.approveStatusEn === "Approved"
            );

            setWarningLetterRequestList(approvedList);

            setPagination({
                page: 1,
                size,
                total: approvedList.length,
                range: [1, approvedList.length],
            });

        } catch (error) {
            Alert({ type: "error", resultObject: error });
        } finally {
            setIsLoading(false);
        }
    }, [currentCompany, currentEmployee]);

    const modifiedEmployeeOptions = useMemo(() => {
        return employeeOptions.map((opt) => ({
            ...opt,
            label: i18n.language === "en" ? opt.fullNameEn : opt.fullName,
            value: opt.id,
        }));
    }, [i18n.language, employeeOptions]);

    const modifiedReasonOptions = useMemo(() => {
        return companyRegulationOptions.map((opt) => {
            const optionId = opt.id ?? opt._id;
            return {
                ...opt,
                id: optionId,
                value: optionId,
                label: i18n.language === "en" ? opt.nameEn : opt.name,
            };
        });
    }, [i18n.language, companyRegulationOptions]);
    const getEmployeeOptions = useCallback(async () => {
        try {
            const response = await commonServices.getDropDown({ companyId: currentCompany });
            setEmployeeOptions(response?.data || []);
        } catch (error) {
            Alert({ type: "error", resultObject: error });
        }
    }, [currentCompany]);

    const getReasonOptions = useCallback(async () => {
        try {
            const response = await commonServices.getCompanyRegulationOptions({
                companyId: currentCompany,
            });
            setCompanyRegulationOptions(response?.data || []);
        } catch (error) {
            Alert({ type: "error", resultObject: error });
        }
    }, [currentCompany]);

    const onOpenCreateModal = useCallback(() => {
        setEditingRecord(null);
        setModalMode("create");
        setIsModalOpen(true);
    }, []);


    const onView = useCallback(async (record) => {
        try {
            setIsLoading(true);
            const response = await warningLetterServices.getWarningLetterRequestById({
                companyId: currentCompany,
                employeeId: currentEmployee,
                warningLetterRequestId: record.warningLetterRequestId,
            });
            const data = response?.data || {};

            const getPositionName = (obj = {}) =>
                i18n.language === "en" ? obj?.positionNameEn : obj?.positionName;

            const mapped = {
                ...data,
                warningLetterRequestId: data.warningLetterRequestId,
                employeeId: data.warnedEmployee?.id,
                position: getPositionName(data.warnedEmployee),
                subject: data.companyRegulation?.id,
                subjectName:
                    i18n.language === "en"
                        ? data.companyRegulation?.nameEn
                        : data.companyRegulation?.name,
                incidentStartDate: toDate(data.incidentDate),
                incidentEndDate: toDate(data.incidentEndDate),
                issueDate: toDate(data.issueDate),
                reasons: data.reasons || [],
                otherReasonDetail: data.otherReasonDetail,
                incidentDetail: data.incidentDetail,
                effectOnCompany: data.effectOnCompany,
                authorizedSigner: {
                    employeeId: data.authorizedSigner?.id,
                    positionName: data.authorizedSigner?.positionName,
                    positionNameEn: data.authorizedSigner?.positionNameEn,
                    signDate: toDate(data.authorizedSignerSignDate),
                    useIssueDateAsSignDate: data.useIssueDateAsSignDate,
                },
                supervisor: {
                    employeeId: data.supervisor?.id,
                    positionName: data.supervisor?.positionName,
                    positionNameEn: data.supervisor?.positionNameEn,
                    signDate: toDate(data.supervisorSignDate),
                    unspecified: data.supervisorUnspecified,
                },
                witness: {
                    employeeId: data.witness?.id,
                    positionName: data.witness?.positionName,
                    positionNameEn: data.witness?.positionNameEn,
                    signDate: toDate(data.witnessSignDate),
                    unspecified: data.witnessUnspecified,
                },
                acknowledgedEmployee: {
                    receivedDate: toDate(data.acknowledgedReceivedDate),
                    notSignedYet: data.acknowledgedNotSignedYet,
                },
                startDate: toDate(data.startDate),
                endDate: toDate(data.endDate),
                createDate: toDate(data.createDate),
                effectiveDate: toDate(data.effecttiveDate ?? data.effectiveDate),
            };

            setEditingRecord(mapped);
            setModalMode("view");
            setIsModalOpen(true);
        } catch (error) {
            Alert({ type: "error", resultObject: error });
        } finally {
            setIsLoading(false);
        }
    }, [currentCompany, currentEmployee, i18n.language]);

    const onCloseModal = useCallback(() => {
        setIsModalOpen(false);
        setEditingRecord(null);
        setModalMode(null);
    }, []);

    const DATE_FORMAT = "YYYY-MM-DD";
    const fmt = (d) => (d && dayjs.isDayjs(d) ? d.format(DATE_FORMAT) : d);

    const buildRequestBody = useCallback(
        (values, status) => {
            const {
                reasons = [],
                otherReasonDetail,
                subject,
                employeeId,
                authorizedSigner = {},
                supervisor = {},
                witness = {},
                acknowledgedEmployee = {},
                incidentStartDate,
                incidentEndDate,
                issueDate,
                startDate,
                endDate,
                effectiveDate,
                ...rest
            } = values;

            return {
                requesterId: currentEmployee,
                status,
                reasons: reasons.filter((r) => r !== OTHER_REASON_ID),
                otherReasonDetail: reasons.includes(OTHER_REASON_ID) ? otherReasonDetail : "",
                companyRegulationId: subject,

                warnedEmployeeId: employeeId,
                witnessId: witness?.employeeId,

                incidentDate: fmt(incidentStartDate),
                incidentEndDate: fmt(incidentEndDate),
                issueDate: fmt(issueDate),
                startDate: fmt(startDate),
                endDate: fmt(endDate),
                effectiveDate: fmt(effectiveDate),


                authorizedSignerId: authorizedSigner?.employeeId,
                authorizedSignerSignDate: fmt(authorizedSigner?.signDate),
                useIssueDateAsSignDate: authorizedSigner?.useIssueDateAsSignDate,

                supervisorId: supervisor?.employeeId,
                supervisorSignDate: fmt(supervisor?.signDate),
                supervisorUnspecified: supervisor?.unspecified,

                witnessSignDate: fmt(witness?.signDate),
                witnessUnspecified: witness?.unspecified,

                acknowledgedReceivedDate: fmt(acknowledgedEmployee?.receivedDate),
                acknowledgedNotSignedYet: acknowledgedEmployee?.notSignedYet,

                ...rest,

                companyId: currentCompany,
                warningLetterRequestId: editingRecord?.warningLetterRequestId,
            };
        },
        [currentCompany, currentEmployee, editingRecord]
    );

    const refreshList = useCallback(() => {
        const modifiedParams = toParams({
            pagination: { page: pagination.page - 1 || 0, pageSize: pagination.size || 10 },
        });
        getWarningLetterRequestList(modifiedParams.pagination);
    }, [pagination, getWarningLetterRequestList]);

    const onSaveDraft = useCallback(
        async (values) => {
            try {
                setIsLoading(true);
                const requestBody = buildRequestBody(values, "DRAFT");
                if (editingRecord) {
                    await warningLetterServices.updateWarningLetterRequest(requestBody);
                } else {
                    await warningLetterServices.createWarningLetterRequest(requestBody);
                }
                Alert({ message: "Success" });
                onCloseModal();
                refreshList();
            } catch (error) {
                Alert({ type: "error", resultObject: error });
            } finally {
                setIsLoading(false);
            }
        },

        [buildRequestBody, editingRecord, onCloseModal, refreshList]
    );

    const onSubmit = useCallback(
        async (values) => {
            try {
                setIsLoading(true);
                const requestBody = buildRequestBody(values, "PENDING");
                if (editingRecord) {
                    await warningLetterServices.updateWarningLetterRequest(requestBody);
                } else {
                    await warningLetterServices.createWarningLetterRequest(requestBody);
                }
                Alert({ message: "Success" });
                onCloseModal();
                refreshList();
            } catch (error) {
                Alert({ type: "error", resultObject: error });
            } finally {
                setIsLoading(false);
            }
        },
        [buildRequestBody, editingRecord, onCloseModal, refreshList]
    );

    const handleCancel = useCallback(
        async (record) => {
            try {
                setIsLoading(true);
                await warningLetterServices.manageWarningLetterRequestStatus({
                    companyId: currentCompany,
                    employeeId: currentEmployee,

                    warningLetterRequestId: record.warningLetterRequestId,
                    sendingType: "CANCELLED",
                });
                Alert({ message: "Success" });
                refreshList();
            } catch (error) {
                Alert({ type: "error", resultObject: error });
            } finally {
                setIsLoading(false);
            }
        },
        [currentCompany, currentEmployee, refreshList]
    );

    const onCancelRequest = useCallback(
        (record) => {
            Modal.confirm({
                title: "Confirmation",
                content: <p>{t("warning_letter.confirm_cancel") || "Are you sure you want to cancel this warning letter?"}</p>,
                onOk() {
                    handleCancel(record);
                },
                okText: "CONFIRM",
                cancelText: "CANCEL",
            });
        },
        [handleCancel, t]
    );

    const onViewPdf = useCallback(async (record) => {
        try {
            setIsLoading(true);
            const detailResponse = await warningLetterServices.getWarningLetterRequestById({
                companyId: currentCompany,
                employeeId: currentEmployee,
                warningLetterRequestId: record.warningLetterRequestId,
            });
            const data = detailResponse?.data || {};

            const payload = {
                companyId: currentCompany,
                lang: i18n.language,
                warnedEmployeeId: data.warnedEmployee?.id,
                subject: data.companyRegulation?.id,
                incidentStartDate: data.incidentDate,
                incidentEndDate: data.incidentEndDate,
                incidentDetail: data.incidentDetail,
                issueDate: data.issueDate,
                reasons: data.reasons,
                otherReasonDetail: data.otherReasonDetail,
                authorizedSigner: {
                    employeeId: data.authorizedSigner?.id,
                    signDate: data.authorizedSignerSignDate,
                    useIssueDateAsSignDate: data.useIssueDateAsSignDate,
                },
                supervisor: {
                    employeeId: data.supervisor?.id,
                    signDate: data.supervisorSignDate,
                    unspecified: data.supervisorUnspecified,
                },
                witness: {
                    employeeId: data.witness?.id,
                    signDate: data.witnessSignDate,
                    unspecified: data.witnessUnspecified,
                },
                acknowledgedEmployee: {
                    receivedDate: data.acknowledgedReceivedDate,
                    notSignedYet: data.acknowledgedNotSignedYet,
                },
            };

            const response = await warningLetterServices.previewWarningLetterPdf(payload);
            const url = window.URL.createObjectURL(
                new Blob([response.data], { type: "application/pdf" })
            );
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Warning-Letter-${record.sequence}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            Alert({ type: "error", resultObject: error });
        } finally {
            setIsLoading(false);
        }
    }, [currentCompany, currentEmployee, i18n.language]);

    const onPreviewPdf = useCallback(async (values) => {
        try {
            setIsLoading(true);
            const payload = {
                companyId: currentCompany,
                lang: i18n.language,
                warnedEmployeeId: values.employeeId,
                subject: values.subject,
                incidentStartDate: values.incidentStartDate?.format("YYYY-MM-DD"),
                incidentEndDate: values.incidentEndDate?.format("YYYY-MM-DD"),
                issueDate: values.issueDate?.format("YYYY-MM-DD"),
                incidentDetail: values.incidentDetail,
                reasons: values.reasons,
                otherReasonDetail: values.otherReasonDetail,
                authorizedSigner: {
                    employeeId: values.authorizedSigner?.employeeId,
                    signDate: values.authorizedSigner?.signDate?.format("YYYY-MM-DD"),
                    useIssueDateAsSignDate: values.authorizedSigner?.useIssueDateAsSignDate,
                },
                supervisor: {
                    employeeId: values.supervisor?.employeeId,
                    signDate: values.supervisor?.signDate?.format("YYYY-MM-DD"),
                    unspecified: values.supervisor?.unspecified,
                },
                witness: {
                    employeeId: values.witness?.employeeId,
                    signDate: values.witness?.signDate?.format("YYYY-MM-DD"),
                    unspecified: values.witness?.unspecified,
                },
                acknowledgedEmployee: {
                    receivedDate: values.acknowledgedEmployee?.receivedDate?.format("YYYY-MM-DD"),
                    notSignedYet: values.acknowledgedEmployee?.notSignedYet,
                },
            };

            const response = await warningLetterServices.previewWarningLetterPdf(payload);
            const url = window.URL.createObjectURL(
                new Blob([response.data], { type: "application/pdf" })
            );
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', "Warning-Letter-Preview.pdf");
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            Alert({ type: "error", resultObject: error });
        } finally {
            setIsLoading(false);
        }
    }, [currentCompany, i18n.language]);

    const onChange = useCallback(
        (tablePagination) => {
            getWarningLetterRequestList({
                page: (tablePagination.current || 1) - 1,
                size: tablePagination.pageSize || 10,
            });
        },
        [getWarningLetterRequestList]
    );

    useEffect(() => {
        if (currentCompany) {
            getWarningLetterRequestList({ page: 0, size: 10 });
            getEmployeeOptions();
            getReasonOptions();
        }
    }, [currentCompany, getWarningLetterRequestList, getEmployeeOptions, getReasonOptions]);

    return {
        isLoading,
        dataSource: warningLetterRequestList,
        pagination,
        onChange,
        isModalOpen,
        editingRecord,
        modalMode,
        onOpenCreateModal,
        onView,
        onCloseModal,
        onCancelRequest,
        onSaveDraft,
        onSubmit,
        onPreviewPdf,
        onViewPdf,
        modifiedEmployeeOptions,
        modifiedReasonOptions,
    };
}

export default useWarningLetterRequest;